import styles from 'css/src/components/common/mentions/ReferenceMention.module.scss';

import React from 'react';
import { useDispatch } from 'react-redux';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { PlainTextPlugin } from '@lexical/react/LexicalPlainTextPlugin';
import {
    $createTextNode,
    $getNodeByKey,
    $getRoot,
    $getSelection,
    $isRangeSelection,
    $isTextNode,
    COMMAND_PRIORITY_LOW,
    KEY_ARROW_UP_COMMAND,
    KEY_ENTER_COMMAND,
} from 'lexical';

import ReferenceBrowserModal from './ReferenceBrowserModal';
import type { Reference, ReferenceKind } from './ReferenceMentionNode';
import {
    $createReferenceMentionNode,
    $isReferenceMentionNode,
    ReferenceMentionNode,
} from './ReferenceMentionNode';
import {
    $populateEditorFromMessage,
    $serializeEditorToMessage,
} from './referenceMentionSerialization';
import ReferenceMentionPopoverPlugin from './ReferenceMentionPopoverPlugin';
import ReferenceMentionsPlugin from './ReferenceMentionsPlugin';

import type { AIVoice, Template } from 'js/src/client';
import type { TemplatePickerModalContext, VoiceSelectionModalContext } from 'js/src/data/modals';
import { hideModal, MODAL_IDS, showModal, VOICE_SELECTION_MODAL_SOURCE } from 'js/src/data/modals';
import type { ImmutableMap } from 'js/src/libs/consts';
import { ImmutableFromJS } from 'js/src/libs/consts';
import { ErrorReporter } from 'js/src/libs/error-reporter';

// Every kind with a browser, matching the "See all" rows. Brand kits have none:
// an account has few enough to pick from the dropdown.
const BROWSE_IN_MODAL_KINDS: ReferenceKind[] = ['video', 'blueprint', 'template', 'voice'];

/** The mention being swapped out. Carries its kind, since a swap can cross
 * kinds — a blueprint for a video, say — and the old node is found by both. */
interface ReplacedMention {
    kind: ReferenceKind;
    id: string;
}

interface PendingInsert {
    reference: Reference;
    /** When set, that mention is replaced rather than a new one appended. */
    replacing: ReplacedMention | null;
    /** The clicked pill's node, so a swap can't hit another mention of the
     * same object. */
    replacingKey: string | null;
    /** The typed "@query" the browser was opened from, still in the draft. */
    triggerText: string | null;
}

/** `value` is always the plain message string, with `@[kind:id|Name]` tokens. */
const ControlledValuePlugin: React.FC<{
    value: string;
    onChange: (value: string) => void;
}> = ({ value, onChange }) => {
    const [editor] = useLexicalComposerContext();
    const lastValueRef = React.useRef<string | null>(null);

    React.useEffect(() => {
        if (value === lastValueRef.current) {
            return;
        }
        lastValueRef.current = value;
        editor.update(() => {
            $populateEditorFromMessage(value);
            // Put the caret at the end so seeded text can be typed straight on.
            $getRoot().selectEnd();
        });
    }, [editor, value]);

    React.useEffect(() => {
        return editor.registerUpdateListener(({ editorState, dirtyElements, dirtyLeaves }) => {
            if (dirtyElements.size === 0 && dirtyLeaves.size === 0) {
                return;
            }
            const serialized = editorState.read(() => $serializeEditorToMessage());
            if (serialized === lastValueRef.current) {
                return;
            }
            lastValueRef.current = serialized;
            onChange(serialized);
        });
    }, [editor, onChange]);

    return null;
};

/** Enter submits, shift+Enter inserts a newline — matching the chat textarea. */
const SubmitOnEnterPlugin: React.FC<{ onSubmit: () => void }> = ({ onSubmit }) => {
    const [editor] = useLexicalComposerContext();

    React.useEffect(() => {
        return editor.registerCommand(
            KEY_ENTER_COMMAND,
            (event: KeyboardEvent | null) => {
                if (event?.shiftKey) {
                    return false;
                }
                // The typeahead registers at the same priority; when its menu
                // is open Enter belongs to the menu, not to submitting.
                if (document.querySelector('[data-reference-mentions-menu] > li')) {
                    return false;
                }
                event?.preventDefault();
                onSubmit();
                return true;
            },
            COMMAND_PRIORITY_LOW,
        );
    }, [editor, onSubmit]);

    return null;
};

/** ArrowUp with an empty draft recalls the last sent message, matching the textarea. */
const RecallOnArrowUpPlugin: React.FC<{ onRecall: () => void }> = ({ onRecall }) => {
    const [editor] = useLexicalComposerContext();

    React.useEffect(() => {
        return editor.registerCommand(
            KEY_ARROW_UP_COMMAND,
            (event: KeyboardEvent | null) => {
                // While the typeahead menu is open, ArrowUp belongs to the menu.
                if (document.querySelector('[data-reference-mentions-menu] > li')) {
                    return false;
                }
                if ($getRoot().getTextContent().trim() !== '') {
                    return false;
                }
                event?.preventDefault();
                onRecall();
                return true;
            },
            COMMAND_PRIORITY_LOW,
        );
    }, [editor, onRecall]);

    return null;
};

/**
 * Splits `text` out of the node before the caret into its own node, if it is
 * there. The typeahead's split node can't be carried across the browser
 * hand-off instead: Lexical merges it back into its neighbours once the
 * typeahead's update ends, invalidating the node.
 */
function $splitOutTextBeforeCaret(text: string) {
    const selection = $getSelection();
    if (!$isRangeSelection(selection) || !selection.isCollapsed()) {
        return null;
    }
    const anchorNode = selection.anchor.getNode();
    const caretOffset = selection.anchor.offset;
    if (!$isTextNode(anchorNode) || $isReferenceMentionNode(anchorNode)) {
        return null;
    }
    if (!anchorNode.getTextContent().slice(0, caretOffset).endsWith(text)) {
        return null;
    }
    return anchorNode
        .splitText(caretOffset - text.length, caretOffset)
        .find((piece) => piece.getTextContent() === text);
}

/**
 * Inserts (or swaps in) a mention chosen outside the editor. Runs inside the
 * composer so it can reach the editor instance.
 */
const InsertMentionPlugin: React.FC<{
    pending: PendingInsert | null;
    onInserted: () => void;
}> = ({ pending, onInserted }) => {
    const [editor] = useLexicalComposerContext();

    React.useEffect(() => {
        if (!pending) {
            return;
        }
        const { reference, replacing, replacingKey, triggerText } = pending;
        editor.update(() => {
            const node = $createReferenceMentionNode(reference);
            const byKey = replacingKey ? $getNodeByKey(replacingKey) : null;
            const existing = $isReferenceMentionNode(byKey)
                ? byKey
                : replacing
                  ? $getRoot()
                        .getAllTextNodes()
                        .find(
                            (candidate) =>
                                $isReferenceMentionNode(candidate) &&
                                candidate.getReferenceKind() === replacing.kind &&
                                candidate.getReferenceId() === replacing.id,
                        )
                  : undefined;

            if (existing) {
                existing.replace(node);
                node.selectNext();
                return;
            }

            // The "@query" the browser was opened from still sits just before
            // the caret; replace it where it is, exactly as picking from the
            // menu would.
            const queryNode = triggerText ? $splitOutTextBeforeCaret(triggerText) : null;
            if (queryNode) {
                queryNode.replace(node);
                const spaceNode = $createTextNode(' ');
                node.insertAfter(spaceNode);
                spaceNode.select();
            } else {
                const spaceNode = $createTextNode(' ');
                $getRoot().selectEnd();
                const selection = $getSelection();
                selection?.insertNodes([node, spaceNode]);
            }
        });
        editor.focus();
        onInserted();
    }, [editor, pending, onInserted]);

    return null;
};

/**
 * A contenteditable has no placeholder of its own (the visible one is a sibling
 * element), but carrying the attribute keeps callers that find the input by its
 * placeholder text working against the textarea this replaces.
 */
const PlaceholderAttributePlugin: React.FC<{ placeholder: string }> = ({ placeholder }) => {
    const [editor] = useLexicalComposerContext();
    React.useEffect(() => {
        return editor.registerRootListener((rootElement) => {
            rootElement?.setAttribute('placeholder', placeholder);
        });
    }, [editor, placeholder]);
    return null;
};

/** Publishes focus() to a ref, so a caller outside the composer can use it. */
const ExposeEditorRefPlugin: React.FC<{ handleRef: React.Ref<MentionChatEditorRef> }> = ({
    handleRef,
}) => {
    const [editor] = useLexicalComposerContext();
    React.useImperativeHandle(handleRef, () => ({ focus: () => editor.focus() }), [editor]);
    return null;
};

const AutoFocusPlugin: React.FC = () => {
    const [editor] = useLexicalComposerContext();
    React.useEffect(() => {
        editor.focus();
    }, [editor]);
    return null;
};

/**
 * `initialConfig.editable` only applies at mount, so a surface whose disabled
 * state resolves later would otherwise stay editable.
 */
const EditablePlugin: React.FC<{ disabled: boolean }> = ({ disabled }) => {
    const [editor] = useLexicalComposerContext();
    React.useEffect(() => {
        editor.setEditable(!disabled);
    }, [editor, disabled]);
    return null;
};

interface MentionChatEditorProps {
    value: string;
    onChange: (value: string) => void;
    onSubmit: () => void;
    references: Reference[];
    placeholder: string;
    autoFocus?: boolean;
    ariaLabel?: string;
    disabled?: boolean;
    /** Called when ArrowUp is pressed on an empty draft, to recall the last message. */
    onHistoryRecall?: () => void;
    /** Extra Lexical plugins, rendered inside the composer. */
    children?: React.ReactNode;
    ref?: React.Ref<MentionChatEditorRef>;
}

export interface MentionChatEditorRef {
    focus: () => void;
}

const MentionChatEditor: React.FC<MentionChatEditorProps> = ({
    value,
    onChange,
    onSubmit,
    references,
    placeholder,
    autoFocus = false,
    ariaLabel = 'Message',
    disabled = false,
    onHistoryRecall,
    children,
    ref,
}) => {
    const dispatch = useDispatch();
    const [browser, setBrowser] = React.useState<{
        kind: ReferenceKind;
        replacing: ReplacedMention | null;
        replacingKey: string | null;
        triggerText: string | null;
    } | null>(null);
    const [pendingInsert, setPendingInsert] = React.useState<PendingInsert | null>(null);

    const openBrowser = React.useCallback(
        (
            kind: ReferenceKind,
            replacingId?: string,
            replacingKey?: string | null,
            triggerText?: string | null,
        ) => {
            const replacing = replacingId ? { kind, id: replacingId } : null;
            const insert = (reference: Reference) => {
                setPendingInsert({
                    reference,
                    replacing,
                    replacingKey: replacingKey ?? null,
                    triggerText: triggerText ?? null,
                });
            };

            if (kind === 'template') {
                dispatch(
                    showModal({
                        modal_id: MODAL_IDS.TEMPLATE_PICKER,
                        modal_context: ImmutableFromJS<TemplatePickerModalContext>({
                            onSelect: (template: ImmutableMap<Template>) =>
                                insert({
                                    kind: 'template',
                                    id: template.get('id'),
                                    name: template.get('display_name'),
                                    thumbUrl: template.get('image_url'),
                                }),
                        }),
                    }),
                );
                return;
            }

            if (kind === 'voice') {
                dispatch(
                    showModal({
                        modal_id: MODAL_IDS.AI_VOICE_SELECTION,
                        modal_context: ImmutableFromJS<VoiceSelectionModalContext>({
                            source: VOICE_SELECTION_MODAL_SOURCE.SCRIPT_EDIT_PAGE,
                            initialSelectedVoiceId: replacingId ?? null,
                            initialVoiceSpeed: null,
                            onApply: (voice: AIVoice) => {
                                // The voice modal leaves closing to its caller.
                                dispatch(hideModal());
                                insert({
                                    kind: 'voice',
                                    id: voice.slug,
                                    name: voice.display_name,
                                    detail: voice.attributes?.accent?.[0] ?? null,
                                    previewUrl: voice.preview_url,
                                });
                            },
                        }),
                    }),
                );
                return;
            }

            if (kind === 'video' || kind === 'blueprint') {
                setBrowser({
                    kind,
                    replacing,
                    replacingKey: replacingKey ?? null,
                    triggerText: triggerText ?? null,
                });
            }
        },
        [dispatch],
    );

    const browseFromTypeahead = React.useCallback(
        (kind: ReferenceKind, triggerText: string | null) => {
            openBrowser(kind, undefined, null, triggerText);
        },
        [openBrowser],
    );

    const onLexicalError = React.useCallback((error: Error) => {
        ErrorReporter.capture(error);
    }, []);

    return (
        <LexicalComposer
            initialConfig={{
                editable: !disabled,
                namespace: 'create-mention-chat',
                onError: onLexicalError,
                nodes: [ReferenceMentionNode],
            }}
        >
            <div className={styles['mention-chat-editor']}>
                <PlainTextPlugin
                    contentEditable={
                        <ContentEditable
                            className={styles['mention-chat-editor__content']}
                            ariaLabel={ariaLabel}
                            aria-disabled={disabled || undefined}
                            // Required alongside the rendered placeholder below.
                            aria-placeholder={placeholder}
                            placeholder={
                                <div
                                    className={`${styles['mention-chat-editor__placeholder']} pe-none`}
                                >
                                    {placeholder}
                                </div>
                            }
                        />
                    }
                    ErrorBoundary={LexicalErrorBoundary}
                />
                <PlaceholderAttributePlugin placeholder={placeholder} />
                <ExposeEditorRefPlugin handleRef={ref ?? null} />
                <ControlledValuePlugin value={value} onChange={onChange} />
                <SubmitOnEnterPlugin onSubmit={onSubmit} />
                {onHistoryRecall && <RecallOnArrowUpPlugin onRecall={onHistoryRecall} />}
                <EditablePlugin disabled={disabled} />
                {autoFocus && !disabled && <AutoFocusPlugin />}
                <ReferenceMentionsPlugin references={references} onSeeAll={browseFromTypeahead} />
                <ReferenceMentionPopoverPlugin
                    references={references}
                    browseKinds={BROWSE_IN_MODAL_KINDS}
                    onBrowse={openBrowser}
                />
                <InsertMentionPlugin
                    pending={pendingInsert}
                    onInserted={() => setPendingInsert(null)}
                />
                {children}
            </div>

            <ReferenceBrowserModal
                showing={browser !== null}
                references={references}
                kind={browser?.kind ?? 'video'}
                selectedId={browser?.replacing?.id}
                onSelect={(reference) => {
                    // Carried on the pending insert: closing the modal clears
                    // `browser` in the same update, so reading it from there
                    // would come back null and append a second mention.
                    setPendingInsert({
                        reference,
                        replacing: browser?.replacing ?? null,
                        replacingKey: browser?.replacingKey ?? null,
                        triggerText: browser?.triggerText ?? null,
                    });
                    setBrowser(null);
                }}
                onHidden={() => setBrowser(null)}
            />
        </LexicalComposer>
    );
};

export default MentionChatEditor;
